/**
 * CounterAnimation Module
 *
 * Animates number counters when they scroll into view.
 * Reads the target value from `data-counter-target` and an optional
 * suffix from `data-counter-suffix` (e.g. "+", "%", "h").
 *
 * @module CounterAnimation
 */
export default class CounterAnimation {

  static DEFAULTS = {
    selector: '[data-counter-target]',
    duration: 2000,
    threshold: 0.3,
    rootMargin: '0px 0px -40px 0px',
  };

  constructor(options = {}) {
    this.config = { ...CounterAnimation.DEFAULTS, ...options };

    this.elements = document.querySelectorAll(this.config.selector);
    if (!this.elements.length) return;

    this._observer = null;
    this._init();
  }

  _init() {
    this._observer = new IntersectionObserver(
      this._handleIntersection.bind(this),
      {
        threshold: this.config.threshold,
        rootMargin: this.config.rootMargin,
      },
    );

    this.elements.forEach((el) => this._observer.observe(el));
  }

  _handleIntersection(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      this._animate(entry.target);
      this._observer.unobserve(entry.target);
    });
  }

  _animate(el) {
    const target = parseFloat(el.getAttribute('data-counter-target'));
    const suffix = el.getAttribute('data-counter-suffix') || '';
    const isInteger = Number.isInteger(target);
    const start = 0;
    const startTime = performance.now();

    const tick = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / this.config.duration, 1);

      // Ease-out cubic for a satisfying deceleration
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = start + (target - start) * eased;

      el.textContent = (isInteger ? Math.round(current) : current.toFixed(1)) + suffix;

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  }

  destroy() {
    if (this._observer) {
      this._observer.disconnect();
    }
  }
}
